import {
  CloseOutlined,
  SelectDS,
  Spacer,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { SidePanelDS } from "@qivia/ui";
import { Option } from "@qivia/ui/src/designSystem/components/Select";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  getDepartmentsByCompanyAsync,
  selectDepartments,
  selectGetDepartmentsByCompanyStatus,
  updateManagerDepartmentsAccessAsync,
} from "../../departments/departmentsSlice";
import { ManagerRole } from "../usersAPI";
import "../../../../i18n/i18n";

export const SidePanelUserDepartments = (props: {
  manager: {
    role: ManagerRole;
    departmentsAccess: string[] | null;
    uuid: string;
  };
  companyUuid: string;
  currentManagerRole: ManagerRole;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const departments = useAppSelector(selectDepartments);
  const getDepartmentsStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );

  useEffect(() => {
    if (props.companyUuid && getDepartmentsStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(props.companyUuid));
    }
  }, [dispatch, getDepartmentsStatus, props.companyUuid]);

  const managerDepartements: Set<string> = useMemo(
    () => new Set(props.manager.departmentsAccess ?? []),
    [props.manager.departmentsAccess],
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d.uuid,
        label: d.name,
        disabled: managerDepartements.has(d.uuid),
      })),
    [departments, managerDepartements],
  );

  const updateManagerDepartmentAccess = useCallback(
    (departmentsAccess: string[]) => {
      if (!props.manager.uuid || !props.companyUuid) {
        return;
      }
      void dispatch(
        updateManagerDepartmentsAccessAsync({
          managerUuid: props.manager.uuid,
          companyUuid: props.companyUuid,
          departmentsAccess,
        }),
      );
    },
    [props.manager.uuid, props.companyUuid, dispatch],
  );

  const onDepartmentSelect = useCallback(
    (option: Option<string> | null) => {
      if (!option || option.disabled) {
        return;
      }
      if (managerDepartements.has(option.value)) {
        return;
      }
      managerDepartements.add(option.value);
      updateManagerDepartmentAccess([...managerDepartements]);
    },
    [managerDepartements, updateManagerDepartmentAccess],
  );

  const onRemoveDepartment = useCallback(
    (departmentUuid: string) => {
      managerDepartements.delete(departmentUuid);
      updateManagerDepartmentAccess([...managerDepartements]);
    },
    [managerDepartements, updateManagerDepartmentAccess],
  );

  const canEditDepartments = useMemo(
    () => props.currentManagerRole === "OWNER",
    [props.currentManagerRole],
  );

  if (
    props.manager.role !== "DEPARTMENT_USER" ||
    props.manager.departmentsAccess == null
  ) {
    return null;
  }

  return (
    <SidePanelDS.Section horizontalSpacing={1.5} verticalSpacing={1.5}>
      <TextCapitalized>
        <StyledSectionTitle>
          {t("users.management.sidePanel.departments.title")}
        </StyledSectionTitle>
      </TextCapitalized>
      <Spacer y={1} />
      {canEditDepartments && (
        <>
          <SelectDS
            label=""
            placeholder={t(
              "users.management.sidePanel.departments.selectPlaceholder",
            )}
            options={departmentOptions}
            value={null}
            onChange={onDepartmentSelect}
            allWidth
            withoutSpacerBottom
            emptyErrorMessage={t(
              "users.management.sidePanel.departments.emptyErrorMessage",
            )}
          />
          <Spacer y={0.5} />
        </>
      )}
      <Spacer y={0.5} />
      <StyledFlex>
        <Spacer x={0.25} />
        <StyledChipRow>
          {props.manager.departmentsAccess.map((uuid) => {
            const departmentName = departments.find(
              (d) => d.uuid === uuid,
            )?.name;
            if (!departmentName) {
              return;
            }
            return (
              <Tag
                key={uuid}
                text={departmentName}
                textColor={colors["colors/text/darkGrey"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                borderColor={colors["colors/borders/cells/cells"]}
                iconRight={
                  canEditDepartments ? (
                    <StyledRemoveIcon
                      onClick={() => {
                        onRemoveDepartment(uuid);
                      }}
                    >
                      <CloseOutlined />
                    </StyledRemoveIcon>
                  ) : undefined
                }
              />
            );
          })}
        </StyledChipRow>
        <Spacer x={0.25} />
      </StyledFlex>
    </SidePanelDS.Section>
  );
};

const StyledFlex = styled.div`
  display: flex;
`;

const StyledSectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
`;

const StyledRemoveIcon = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
  fill: ${colors["colors/text/lightGrey"]};

  &:hover {
    fill: ${colors["colors/text/black"]};
  }
`;
